import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Step, StepLabel, Stepper, Typography } from '@mui/material';
import routes from 'constants/routes';
import { getQuotation, getQuotationOnlyFlights } from 'features/quotation/services';
import { extractErrorMessage } from 'features/quotation/helpers';
import {
	LoadedQuotation,
	QuotationImportStep,
	WizardSavedPassenger,
	WizardSavedService,
	buildPassengerSlots,
	getRoomBaseConfigFromQuotation,
} from '../types';
import { loadQuotationImportState, saveQuotationImportState } from '../sessionStorage';
import { StepQuotationEntry } from './steps/StepQuotationEntry';
import { StepOrderMetadata } from './steps/StepOrderMetadata';
import { StepPassengers } from './steps/StepPassengers';
import { StepFlights } from './steps/StepFlights';
import { StepAccommodations } from './steps/StepAccommodations';
import { StepExtraServices } from './steps/StepExtraServices';
import { StepSummary } from './steps/StepSummary';
import { WizardOrderCreatedBanner } from './WizardOrderCreatedBanner';
import { WizardDealSaleOrderConflict } from './WizardDealSaleOrderConflict';
import { DealSaleOrderConflict, findSaleOrderConflictForDeal } from '../helpers/dealSaleOrderConflict';

const STEP_LABELS: Record<QuotationImportStep, string> = {
	quotation: 'Cotización',
	order: 'Orden',
	passengers: 'Pasajeros',
	flights: 'Vuelo',
	accommodations: 'Hoteles',
	services: 'Servicios',
	summary: 'Resumen',
};

const stepOrder: QuotationImportStep[] = [
	'quotation',
	'order',
	'passengers',
	'flights',
	'accommodations',
	'services',
	'summary',
];

const persist = (
	quotationId: number,
	saleOrderId: string,
	step: QuotationImportStep,
	savedPassengers: WizardSavedPassenger[],
	extra?: Partial<{ currentPassengerSlotIndex: number; savedServices: WizardSavedService[] }>,
) => {
	const existing = loadQuotationImportState(saleOrderId);
	saveQuotationImportState({
		quotationId,
		saleOrderId,
		step,
		savedPassengers,
		currentPassengerSlotIndex: extra?.currentPassengerSlotIndex ?? existing?.currentPassengerSlotIndex ?? 0,
		savedServices: extra?.savedServices ?? existing?.savedServices ?? [],
	});
};

export const QuotationImportWizard = (): JSX.Element => {
	const history = useHistory();
	const location = useLocation();
	const { id: saleOrderIdFromRoute } = useParams<{ id: string }>();
	const isContinuation = Boolean(saleOrderIdFromRoute);

	const quotationIdFromQuery = useMemo(() => {
		const id = Number(new URLSearchParams(location.search).get('quotationId'));
		return Number.isFinite(id) && id > 0 ? id : null;
	}, [location.search]);

	const [activeStep, setActiveStep] = useState<QuotationImportStep>(isContinuation ? 'passengers' : 'quotation');
	const [quotation, setQuotation] = useState<LoadedQuotation | null>(null);
	const [saleOrderId, setSaleOrderId] = useState<string | null>(saleOrderIdFromRoute ?? null);
	const [savedPassengers, setSavedPassengers] = useState<WizardSavedPassenger[]>([]);
	const [savedServices, setSavedServices] = useState<WizardSavedService[]>([]);
	const [loadingResume, setLoadingResume] = useState(isContinuation);
	const [loadingQuotationFromLink, setLoadingQuotationFromLink] = useState(false);
	const [wizardSessionMissing, setWizardSessionMissing] = useState(false);
	const [resumeError, setResumeError] = useState<string | null>(null);
	const [dealSaleOrderConflict, setDealSaleOrderConflict] = useState<DealSaleOrderConflict | null>(null);

	const activeStepIndex = stepOrder.indexOf(activeStep);
	const showOrderCreatedBanner =
		Boolean(saleOrderId && quotation) && activeStep !== 'quotation' && activeStep !== 'order';

	const fetchQuotation = useCallback(async (quotationId: number) => {
		try {
			const res = await getQuotation(quotationId);
			return res.data;
		} catch {
			const res = await getQuotationOnlyFlights(quotationId);
			return res.data;
		}
	}, []);

	useEffect(() => {
		if (!isContinuation || !saleOrderIdFromRoute) return;

		const resume = async () => {
			setLoadingResume(true);
			const stored = loadQuotationImportState(saleOrderIdFromRoute);
			if (!stored) {
				setWizardSessionMissing(true);
				setLoadingResume(false);
				return;
			}

			try {
				const q = await fetchQuotation(stored.quotationId);
				setQuotation(q);
				setSaleOrderId(stored.saleOrderId);
				setSavedPassengers(stored.savedPassengers);
				setSavedServices(stored.savedServices ?? []);

				const requiredSlots = buildPassengerSlots(getRoomBaseConfigFromQuotation(q)).length;
				const passengersDone = stored.savedPassengers.length >= requiredSlots;

				if (!passengersDone) {
					setActiveStep('passengers');
				} else if (stepOrder.indexOf(stored.step) >= 0) {
					setActiveStep(stored.step);
				} else {
					setActiveStep('flights');
				}
			} catch (e) {
				setResumeError(extractErrorMessage(e, 'Error al reanudar la cotización'));
			} finally {
				setLoadingResume(false);
			}
		};

		void resume();
	}, [fetchQuotation, isContinuation, saleOrderIdFromRoute]);

	const applyLoadedQuotation = useCallback(async (q: LoadedQuotation) => {
		setQuotation(q);
		setResumeError(null);
		setDealSaleOrderConflict(null);

		const conflict = await findSaleOrderConflictForDeal(q.crmTicket, q.id);
		if (conflict) {
			setDealSaleOrderConflict(conflict);
			setActiveStep('quotation');
			return;
		}

		setActiveStep('order');
	}, []);

	useEffect(() => {
		if (isContinuation || !quotationIdFromQuery || quotation) {
			return;
		}

		let cancelled = false;

		const loadFromQuery = async () => {
			setLoadingQuotationFromLink(true);
			setResumeError(null);
			setDealSaleOrderConflict(null);
			try {
				const q = await fetchQuotation(quotationIdFromQuery);
				if (cancelled) {
					return;
				}
				await applyLoadedQuotation(q);
			} catch (e) {
				if (!cancelled) {
					setResumeError(extractErrorMessage(e, 'No se encontró la cotización'));
				}
			} finally {
				setLoadingQuotationFromLink(false);
			}
		};

		void loadFromQuery();

		return () => {
			cancelled = true;
		};
	}, [applyLoadedQuotation, fetchQuotation, isContinuation, quotation, quotationIdFromQuery]);

	const handleQuotationLoaded = async (_quotationId: number, loaded: LoadedQuotation) => {
		setLoadingQuotationFromLink(true);
		try {
			await applyLoadedQuotation(loaded);
		} catch (e) {
			setResumeError(extractErrorMessage(e, 'Error al validar la cotización'));
		} finally {
			setLoadingQuotationFromLink(false);
		}
	};

	const handleOrderCreated = (newSaleOrderId: string) => {
		if (!quotation) return;

		persist(quotation.id, newSaleOrderId, 'passengers', [], { currentPassengerSlotIndex: 0, savedServices: [] });

		history.replace(routes.salesOrder.importFromQuotation.replace(':id', newSaleOrderId));
		setSaleOrderId(newSaleOrderId);
		setActiveStep('passengers');
	};

	const handlePassengersComplete = (saved: WizardSavedPassenger[]) => {
		if (!quotation || !saleOrderId) return;

		setSavedPassengers(saved);
		persist(quotation.id, saleOrderId, 'flights', saved);
		setActiveStep('flights');
	};

	const goToStep = (step: QuotationImportStep) => {
		if (!quotation || !saleOrderId) return;
		persist(quotation.id, saleOrderId, step, savedPassengers, { savedServices });
		setActiveStep(step);
	};

	if (loadingResume || loadingQuotationFromLink) {
		return (
			<Box display="flex" justifyContent="center" py={8}>
				<CircularProgress />
			</Box>
		);
	}

	if (dealSaleOrderConflict) {
		return (
			<WizardDealSaleOrderConflict
				conflict={dealSaleOrderConflict}
				onBackToQuotations={() => history.push(routes.quotations.list)}
			/>
		);
	}

	if (wizardSessionMissing && saleOrderIdFromRoute) {
		const orderUrl = routes.salesOrder.edit.replace(':id', saleOrderIdFromRoute);

		return (
			<Box>
				<Typography variant="h5" fontWeight={700} gutterBottom>
					Nueva orden desde cotización
				</Typography>
				<Alert severity="info" sx={{ mb: 2 }}>
					<Typography variant="body2" component="div" gutterBottom>
						<strong>Esta orden de venta ya existe.</strong> Lo que cargaste (pasajeros, vuelos, hoteles, etc.) quedó
						guardado en el servidor.
					</Typography>
					<Typography variant="body2" component="div">
						El asistente no puede reanudar el paso en este navegador porque se perdió el progreso local (por ejemplo, al
						recargar la página). <strong>Continuá la carga en la pantalla habitual de la orden</strong>— no crees una
						orden nueva desde la cotización para evitar duplicados.
					</Typography>
				</Alert>
				<Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
					<Button variant="contained" color="primary" size="large" onClick={() => history.push(orderUrl)}>
						Ir a la orden de venta
					</Button>
					<Typography variant="caption" color="text.secondary">
						ID: {saleOrderIdFromRoute}
					</Typography>
				</Box>
			</Box>
		);
	}

	if (resumeError && !quotation) {
		const orderUrl = saleOrderIdFromRoute ? routes.salesOrder.edit.replace(':id', saleOrderIdFromRoute) : null;

		return (
			<Box>
				<Typography variant="h5" fontWeight={700} gutterBottom>
					Nueva orden desde cotización
				</Typography>
				<Alert severity="warning" sx={{ mb: 2 }}>
					{resumeError}
				</Alert>
				{orderUrl && (
					<Button variant="contained" onClick={() => history.push(orderUrl)}>
						Ir a la orden de venta
					</Button>
				)}
			</Box>
		);
	}

	return (
		<Box>
			<Typography variant="h5" fontWeight={700} gutterBottom>
				Nueva orden desde cotización
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				Asistente de carga — el flujo clásico de creación de órdenes no se modifica.
			</Typography>

			{resumeError && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setResumeError(null)}>
					{resumeError}
				</Alert>
			)}

			<Stepper activeStep={activeStepIndex} alternativeLabel sx={{ mb: 4 }}>
				{stepOrder.map((key) => (
					<Step key={key}>
						<StepLabel>{STEP_LABELS[key]}</StepLabel>
					</Step>
				))}
			</Stepper>

			{showOrderCreatedBanner && saleOrderId && quotation && (
				<WizardOrderCreatedBanner saleOrderId={saleOrderId} crmTicket={quotation.crmTicket} />
			)}

			{activeStep === 'quotation' && (
				<StepQuotationEntry onQuotationLoaded={(id, loaded) => void handleQuotationLoaded(id, loaded)} />
			)}

			{activeStep === 'order' && quotation && (
				<StepOrderMetadata
					quotation={quotation}
					onOrderCreated={handleOrderCreated}
					onExistingSaleOrder={setDealSaleOrderConflict}
				/>
			)}

			{activeStep === 'passengers' && quotation && saleOrderId && (
				<StepPassengers
					saleOrderId={saleOrderId}
					quotation={quotation}
					initialState={loadQuotationImportState(saleOrderId)}
					onComplete={handlePassengersComplete}
				/>
			)}

			{activeStep === 'flights' && quotation && saleOrderId && (
				<StepFlights
					saleOrderId={saleOrderId}
					quotation={quotation}
					savedPassengers={savedPassengers}
					onComplete={() => goToStep('accommodations')}
					onSkip={() => goToStep('accommodations')}
				/>
			)}

			{activeStep === 'accommodations' && quotation && saleOrderId && (
				<StepAccommodations
					saleOrderId={saleOrderId}
					quotation={quotation}
					savedPassengers={savedPassengers}
					onComplete={() => goToStep('services')}
				/>
			)}

			{activeStep === 'services' && quotation && saleOrderId && (
				<StepExtraServices
					saleOrderId={saleOrderId}
					quotation={quotation}
					savedPassengers={savedPassengers}
					onComplete={() => goToStep('summary')}
				/>
			)}

			{activeStep === 'summary' && quotation && saleOrderId && (
				<StepSummary
					saleOrderId={saleOrderId}
					quotation={quotation}
					savedPassengers={savedPassengers}
					savedServices={savedServices}
				/>
			)}
		</Box>
	);
};
